export function formatNumber(value, options = {}) {
  if (!Number.isFinite(Number(value))) return "—";
  return new Intl.NumberFormat("en-US", options).format(Number(value));
}

export function formatCompact(value, maximumFractionDigits = 1) {
  if (!Number.isFinite(Number(value))) return "—";
  return new Intl.NumberFormat("en-US", {
    notation: Math.abs(Number(value)) >= 10000 ? "compact" : "standard",
    maximumFractionDigits
  }).format(Number(value));
}

export function formatDuration(seconds) {
  if (!Number.isFinite(Number(seconds))) return "—";
  const total = Math.max(0, Math.round(Number(seconds)));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  if (m) return `${m}m`;
  return `${total % 60}s`;
}

export function formatWeight(value, unit = "kg") {
  if (!Number.isFinite(Number(value))) return "—";
  const isLb = ["lb", "lbs", "pound", "pounds"].includes(String(unit).trim().toLowerCase());
  const displayUnit = isLb ? "lb" : "kg";
  return `${formatCompact(Math.round(Number(value)))} ${displayUnit}`;
}

export function formatPercent(value, maximumFractionDigits = 1) {
  if (!Number.isFinite(Number(value))) return "—";
  return `${formatNumber(Number(value) * 100, { maximumFractionDigits })}%`;
}

export function formatDateRange(period) {
  if (!period?.start || !period?.endInclusive) return "";
  const start = new Date(`${period.start}T00:00:00.000Z`);
  const end = new Date(`${period.endInclusive}T00:00:00.000Z`);
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  const shortOpts = { month: "short", day: "numeric" };
  const yearOpts = { month: "short", day: "numeric", year: "numeric" };
  if (sameYear) {
    return `${new Intl.DateTimeFormat("en-US", shortOpts).format(start)} – ${new Intl.DateTimeFormat("en-US", shortOpts).format(end)}`;
  }
  return `${new Intl.DateTimeFormat("en-US", yearOpts).format(start)} – ${new Intl.DateTimeFormat("en-US", yearOpts).format(end)}`;
}

export function formatDayLabel(isoDate) {
  if (!isoDate) return "";
  const day = new Date(`${isoDate}T00:00:00.000Z`);
  if (!Number.isFinite(day.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(day);
}

// Days are compared in UTC, like formatDateRange — a viewer's timezone should
// not decide what counts as "yesterday".
export function formatRelativeDay(value, now = new Date()) {
  const dayMs = 86400000;
  // Guarded before the Date: `new Date(null)` is the epoch, not an invalid date.
  if (!value) return "";
  const then = new Date(value).getTime();
  const today = new Date(now).getTime();
  if (!Number.isFinite(then) || !Number.isFinite(today)) return "";
  const days = Math.floor(today / dayMs) - Math.floor(then / dayMs);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return formatDayLabel(new Date(then).toISOString().slice(0, 10));
}

export function formatPace(value) {
  if (!Number.isFinite(Number(value))) return "—";
  return `${Number(value).toFixed(1)}/wk`;
}

export function deltaSign(direction) {
  if (direction === "up") return "▲";
  if (direction === "down") return "▼";
  return "→";
}

export function detectWeightUnit(workouts) {
  const counts = new Map();
  for (const w of workouts || []) {
    if (!w?.metadata?.weightUnit) continue;
    const u = String(w.metadata.weightUnit).trim().toLowerCase();
    const normalized = ["lb", "lbs", "pound", "pounds"].includes(u) ? "lb" : "kg";
    counts.set(normalized, (counts.get(normalized) || 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || "kg";
}
